import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CheckFormWrapper from '../shared/CheckFormWrapper';
import FileUploadField from '../shared/FileUploadField';
import TagsInput from '../shared/TagsInput';

/**
 * BackupCheckForm
 *
 * Analyses database backup logs and produces three output files:
 *   1. backup_summary    – selected-day backup results
 *   2. failed_dates_log  – all log entries for days that had a failure
 *   3. failed_rerun      – latest entry per failed day with rerun status
 *
 * API: POST /helper/backup-check
 *
 * Props:
 *   onSubmit   - (FormData) => void  called with ready-to-send FormData
 *   isLoading  - bool  disables the submit button while a request is in flight
 */
export default function BackupCheckForm({ onSubmit, isLoading = false }) {
  const [inputFile, setInputFile] = useState(null);
  const [startTimeCol, setStartTimeCol] = useState('');
  const [stateCol, setStateCol] = useState([]);
  const [stateColError, setStateColError] = useState('');
  const [listOfDays, setListOfDays] = useState([]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (stateCol.length !== 2) {
      setStateColError(
        'Add exactly two values: state column name, then success value (e.g. Successful)'
      );
      return;
    }
    setStateColError('');

    const formData = new FormData();
    formData.append('inputFile', inputFile);
    formData.append('startTimeCol', startTimeCol);
    formData.append('stateCol', JSON.stringify(stateCol));
    formData.append(
      'listOfDays',
      JSON.stringify(listOfDays.length ? listOfDays : [1, 20, 30])
    );

    onSubmit(formData);
  };

  return (
    <CheckFormWrapper
      title="Backup Check"
      description="Evaluates backup logs for selected days to determine if backups were successful and identifies any reruns."
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <FileUploadField
        label="Backup Log File"
        file={inputFile}
        onChange={setInputFile}
        required
        helperText="Accepted formats: .csv, .xlsx"
      />

      <TextField
        size="small"
        label="Start Time Column"
        value={startTimeCol}
        onChange={(e) => setStartTimeCol(e.target.value)}
        required
        sx={{ maxWidth: 320 }}
        helperText='Column containing timestamps in "DD.MM.YYYY HH:MM:SS" format'
      />

      <Box>
        <TagsInput
          label={
            <>
              State Column <span style={{ color: '#d32f2f' }}>*</span>
            </>
          }
          values={stateCol}
          onChange={(next) => {
            setStateCol(next);
            if (next.length === 2) setStateColError('');
          }}
          maxValues={2}
          placeholder="First: column name, then: success text"
          helperText="First chip = state column (e.g. State). Second chip = value meaning success (e.g. Successful)."
        />
        {stateColError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {stateColError}
          </Typography>
        )}
      </Box>

      <TagsInput
        label="Days to Sample (Day-of-Month)"
        values={listOfDays}
        onChange={setListOfDays}
        type="number"
        placeholder="Enter a day number (1–31)"
        helperText="Leave empty to use default days: 1, 20, 30"
      />
    </CheckFormWrapper>
  );
}
